import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { SYSTEM_ORDER_TEMPLATES, sanitizeTemplateOrderData } from "../data/orderTemplates";
import {
  createEmptyOrder,
  ensureOrderIds,
  mergeOrder,
  normalizeOrderAfterLoad,
} from "../utils/orderDefaults";
import {
  createOrder as apiCreateOrder,
  fetchOrderById,
  fetchOrdersList,
  resolveSavedOrderId,
  updateOrder as apiUpdateOrder,
} from "../services/pohrebkaApi";

const Pohrebka2Context = createContext(null);

function hydrateLoadedOrder(source, orderId = "") {
  const empty = createEmptyOrder(orderId);
  const loaded = source && typeof source === "object" ? source : {};

  return normalizeOrderAfterLoad({
    ...empty,
    ...loaded,
    meta: {
      ...(empty.meta || {}),
      ...(loaded.meta || {}),
      ...(orderId ? { id: orderId, orderId } : {}),
    },
    zaklad: {
      ...(empty.zaklad || {}),
      ...((loaded && loaded.zaklad) || {}),
    },
    vstupniInformace: {
      ...(empty.vstupniInformace || {}),
      ...((loaded && loaded.vstupniInformace) || {}),
    },
    vypravitel: {
      ...(empty.vypravitel || {}),
      ...((loaded && loaded.vypravitel) || {}),
    },
    zesnulaOsoba: {
      ...(empty.zesnulaOsoba || {}),
      ...((loaded && loaded.zesnulaOsoba) || {}),
    },
    vyjezd: {
      ...(empty.vyjezd || {}),
      ...((loaded && loaded.vyjezd) || {}),
    },
    prevoz: {
      ...(empty.prevoz || {}),
      ...((loaded && loaded.prevoz) || {}),
      etapy: {
        ...(((empty.prevoz || {}).etapy) || {}),
        ...((((loaded && loaded.prevoz) || {}).etapy) || {}),
      },
    },
    obrad: {
      ...(empty.obrad || {}),
      ...((loaded && loaded.obrad) || {}),
    },
    sluzby: Array.isArray(loaded?.sluzby)
      ? loaded.sluzby.map((row) => ({
          id: String(row?.id || "").trim(),
          mnozstvi: Number(row?.mnozstvi || 0) || 0,
          cenaBezDphOverride:
            row?.cenaBezDphOverride === 0 || row?.cenaBezDphOverride === "0"
              ? row.cenaBezDphOverride
              : (row?.cenaBezDphOverride || ""),
          rakevId: row?.rakevId || "",
        })).filter((row) => row.id)
      : Array.isArray(empty?.sluzby)
        ? empty.sluzby
        : [],
  }, orderId);
}

const GLOBAL_CENIK_STORAGE_KEY = "pohrebka2_global_cenik_overrides";

function readGlobalCenikOverrides() {
  try {
    const raw = localStorage.getItem(GLOBAL_CENIK_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function createEmptyOrderWithGlobalCenik(forcedId = "") {
  const base = createEmptyOrder(forcedId);
  const globalOverrides = readGlobalCenikOverrides();
  return ensureOrderIds({
    ...base,
    cenikOverrides: globalOverrides,
  }, forcedId);
}


export function Pohrebka2Provider({ children }) {
  const [activeTab, setActiveTab] = useState("zaklad");
  const [currentOrderId, setCurrentOrderId] = useState("");
  const [order, setOrder] = useState(() => createEmptyOrderWithGlobalCenik());
  const [ordersList, setOrdersList] = useState([]);
  const [customTemplates, setCustomTemplates] = useState(() => {
    try {
      const raw = localStorage.getItem("pohrebka2_custom_templates");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const resetOrder = useCallback(() => {
    setCurrentOrderId("");
    setOrder(createEmptyOrderWithGlobalCenik());
    setActiveTab("zaklad");
    setError("");
  }, []);

  const patchOrder = useCallback((patch) => {
    setOrder((prev) => mergeOrder(prev, patch, currentOrderId || ""));
  }, [currentOrderId]);

  const patchSection = useCallback((sectionName, patch) => {
    setOrder((prev) => {
      const next = {
        ...(prev || {}),
        [sectionName]: {
          ...((prev && prev[sectionName]) || {}),
          ...(patch || {}),
        },
      };
      return ensureOrderIds(next, currentOrderId || "");
    });
  }, [currentOrderId]);

  const persistCustomTemplates = useCallback((next) => {
    setCustomTemplates(next);
    try {
      localStorage.setItem("pohrebka2_custom_templates", JSON.stringify(next || []));
    } catch {}
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const items = await fetchOrdersList();
      setOrdersList(Array.isArray(items) ? items : []);
      return items;
    } catch (e) {
      setError(String(e?.message || e));
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const openOrder = useCallback(async (id) => {
    const zid = String(id || "").trim();
    if (!zid) return null;

    setLoading(true);
    setError("");
    try {
      const loaded = await fetchOrderById(zid);
      const normalized = hydrateLoadedOrder(loaded, zid);
      setCurrentOrderId(zid);
      setOrder(normalized);
      setActiveTab("zaklad");
      return normalized;
    } catch (e) {
      setError(String(e?.message || e));
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const allTemplates = useMemo(() => {
    const custom = Array.isArray(customTemplates) ? customTemplates : [];
    const system = Array.isArray(SYSTEM_ORDER_TEMPLATES) ? SYSTEM_ORDER_TEMPLATES : [];
    return [...system, ...custom];
  }, [customTemplates]);

  const applyTemplateToCurrentOrder = useCallback((templateId) => {
    const tpl = allTemplates.find((item) => String(item?.id || "") === String(templateId || ""));
    if (!tpl?.data) return false;

    setOrder((prev) => {
      const base = prev || {};
      const sanitized = sanitizeTemplateOrderData(tpl.data);

      const templateServices = Array.isArray(sanitized?.sluzby)
        ? sanitized.sluzby.map((row) => ({
            id: String(row?.id || "").trim(),
            mnozstvi: Number(row?.mnozstvi || 1) || 1,
            cenaBezDphOverride:
              row?.cenaBezDphOverride === 0 || row?.cenaBezDphOverride === "0"
                ? row.cenaBezDphOverride
                : (row?.cenaBezDphOverride || ""),
            rakevId: row?.rakevId || "",
          })).filter((row) => row.id)
        : [];

      const merged = hydrateLoadedOrder({
        ...base,
        ...sanitized,
        meta: {
          ...(base?.meta || {}),
          ...(sanitized?.meta || {}),
          selectedTemplateId: String(tpl?.id || ""),
          selectedTemplateName: String(tpl?.name || ""),
        },
        sluzby: templateServices,
      }, currentOrderId || "");

      return merged;
    });

    setActiveTab("zaklad");
    return true;
  }, [allTemplates, currentOrderId]);

  const saveCurrentOrderAsTemplate = useCallback((name) => {
    const safeName = String(name || "").trim();
    if (!safeName) return false;

    const template = {
      id: `tpl_custom_${Date.now()}`,
      name: safeName,
      isSystem: false,
      createdAt: new Date().toISOString(),
      data: sanitizeTemplateOrderData(order || {}),
    };

    const prev = Array.isArray(customTemplates) ? customTemplates : [];
    persistCustomTemplates([template, ...prev]);
    return true;
  }, [order, customTemplates, persistCustomTemplates]);

  const startNewOrder = useCallback(() => {
    resetOrder();
  }, [resetOrder]);

  const saveOrder = useCallback(async (overrideOrder = null) => {
      setSaving(true);
      setError("");

      try {
        const sourceOrder =
          overrideOrder && typeof overrideOrder === "object"
            ? overrideOrder
            : order;

        const payload = ensureOrderIds(sourceOrder, currentOrderId || "");
        let res;

        if (currentOrderId) {
          res = await apiUpdateOrder(currentOrderId, payload);
        } else {
          res = await apiCreateOrder(payload);
        }

        const savedId = resolveSavedOrderId(res) || currentOrderId || "";
        if (!savedId) {
          throw new Error("Nepodařilo se zjistit ID uložené zakázky");
        }

        const fresh = await fetchOrderById(savedId);
        const normalized = hydrateLoadedOrder(fresh, savedId);

        setCurrentOrderId(savedId);
        setOrder(normalized);
        setActiveTab("zaklad");

        try { await loadOrders(); } catch (_e) {}

        return { ...res, savedId, order: normalized };
      } catch (e) {
        setError(String(e?.message || e));
        throw e;
      } finally {
        setSaving(false);
      }
    }, [order, currentOrderId, loadOrders]);

  const value = useMemo(() => ({
    activeTab,
    setActiveTab,

    currentOrderId,
      activeOrderId: currentOrderId,
      setCurrentOrderId,

    order,
    setOrder,
    patchOrder,
    patchSection,

    ordersList,
    customTemplates,
    allTemplates,
    loading,
    saving,
    error,

    loadOrders,
    applyTemplateToCurrentOrder,
    saveCurrentOrderAsTemplate,
    openOrder,
    saveOrder,
    resetOrder,
    startNewOrder,
  }), [
    activeTab,
    currentOrderId,
    order,
    ordersList,
    loading,
    saving,
    error,
    loadOrders,
    openOrder,
    saveOrder,
    resetOrder,
    startNewOrder,
    patchOrder,
    patchSection,
  ]);

  return (
    <Pohrebka2Context.Provider value={value}>
      {children}
    </Pohrebka2Context.Provider>
  );
}

export function usePohrebka2() {
  const ctx = useContext(Pohrebka2Context);
  if (!ctx) {
    throw new Error("usePohrebka2 musí být uvnitř Pohrebka2Provider");
  }
  return ctx;
}
